import { useState } from "react";
import { FileText, Zap, Shield, Users, ArrowRight, CheckCircle2, Upload, Clock, TrendingUp, Globe, Layers, RefreshCw, BarChart3, Database, Lock, GitBranch } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export default function NewLanding({ login, register }) {
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [showStandards, setShowStandards] = useState(false);
  const [formData, setFormData] = useState({ email: "", username: "", password: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLogin) {
      await login(formData.email, formData.password);
    } else {
      await register(formData.email, formData.username, formData.password);
    }
  };

  if (showAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md glass-effect border-none shadow-xl" data-testid="auth-card">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-center mb-4">
              <svg className="h-14 w-14" viewBox="0 0 40 40" fill="none">
                <path d="M20 2L37 12L37 28L20 38L3 28L3 12L20 2Z" fill="url(#standari-auth-gradient)" stroke="#1e40af" strokeWidth="1.5"/>
                <circle cx="20" cy="20" r="3" fill="white"/>
                <circle cx="20" cy="12" r="1.5" fill="white" opacity="0.7"/>
                <circle cx="28" cy="16" r="1.5" fill="white" opacity="0.7"/>
                <circle cx="28" cy="24" r="1.5" fill="white" opacity="0.7"/>
                <circle cx="20" cy="28" r="1.5" fill="white" opacity="0.7"/>
                <circle cx="12" cy="24" r="1.5" fill="white" opacity="0.7"/>
                <circle cx="12" cy="16" r="1.5" fill="white" opacity="0.7"/>
                <defs>
                  <linearGradient id="standari-auth-gradient" x1="0" y1="0" x2="40" y2="40">
                    <stop offset="0%" stopColor="#1e40af"/>
                    <stop offset="50%" stopColor="#3b82f6"/>
                    <stop offset="100%" stopColor="#1e3a8a"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <CardTitle className="text-2xl font-heading text-center">
              {isLogin ? "Welcome Back" : "Request Pilot Access"}
            </CardTitle>
            <CardDescription className="text-center">
              {isLogin ? "Sign in to Standari" : "Professional documentation platform for DoD, Healthcare & Enterprise"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="email-input"
                className="border-slate-300"
              />
              {!isLogin && (
                <Input
                  type="text"
                  placeholder="Username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  data-testid="username-input"
                  className="border-slate-300"
                />
              )}
              <Input
                type="password"
                placeholder="Password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                data-testid="password-input"
                className="border-slate-300"
              />
              <Button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800" data-testid="auth-submit-btn">
                {isLogin ? "Sign In" : "Request Pilot"}
              </Button>
            </form>
            <div className="mt-4 text-center text-sm">
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="text-blue-600 hover:underline"
                data-testid="toggle-auth-mode"
              >
                {isLogin ? "New to Standari? Sign up free" : "Already have an account? Sign in"}
              </button>
            </div>
            <div className="mt-4 text-center">
              <button
                onClick={() => setShowAuth(false)}
                className="text-slate-600 text-sm hover:text-slate-900"
                data-testid="back-to-landing"
              >
                ← Back
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100" data-testid="landing-page">
      {/* Header - Clean & Professional */}
      <header className="fixed w-full top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <svg width="36" height="36" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 2L37 12L37 28L20 38L3 28L3 12L20 2Z" fill="url(#standari-gradient)" stroke="#1e40af" strokeWidth="1.5"/>
                <circle cx="20" cy="20" r="3" fill="white"/>
                <circle cx="20" cy="12" r="1.5" fill="white" opacity="0.7"/>
                <circle cx="28" cy="16" r="1.5" fill="white" opacity="0.7"/>
                <circle cx="28" cy="24" r="1.5" fill="white" opacity="0.7"/>
                <circle cx="20" cy="28" r="1.5" fill="white" opacity="0.7"/>
                <circle cx="12" cy="24" r="1.5" fill="white" opacity="0.7"/>
                <circle cx="12" cy="16" r="1.5" fill="white" opacity="0.7"/>
                <defs>
                  <linearGradient id="standari-gradient" x1="0" y1="0" x2="40" y2="40">
                    <stop offset="0%" stopColor="#1e40af"/>
                    <stop offset="50%" stopColor="#3b82f6"/>
                    <stop offset="100%" stopColor="#1e3a8a"/>
                  </linearGradient>
                </defs>
              </svg>
              <div>
                <span className="text-xl font-heading font-bold text-slate-900">Standari</span>
                <span className="hidden sm:block text-xs text-slate-500">Avoid Fines. Win Contracts.</span>
              </div>
            </div>
            
            {/* Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#pricing" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Pricing</a>
              <a href="mailto:sales@standari.com" className="text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors">Contact Sales</a>
            </nav>

            {/* CTA */}
            <div className="flex items-center space-x-3">
              <Button 
                variant="ghost" 
                onClick={() => setShowAuth(true)} 
                className="hidden sm:inline-flex text-slate-600 hover:text-blue-600"
              >
                Sign In
              </Button>
              <Button 
                onClick={() => setShowAuth(true)} 
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-md"
                data-testid="get-started-header-btn"
              >
                Start Pilot →
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center fade-in">
          <Badge className="mb-6 bg-slate-100 text-slate-900 hover:bg-slate-200 px-4 py-2 border border-slate-300">
            Single Source of Truth
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-slate-900 mb-6">
            Documentation That
            <span className="block bg-gradient-to-r from-blue-600 to-slate-700 bg-clip-text text-transparent mt-2">Stays Current</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-600 max-w-3xl mx-auto mb-8">
            Upload any technical document. Instantly see the matching standard, cost, and next steps.
            <span className="font-semibold text-slate-900"> From local businesses to global enterprises.</span>
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button onClick={() => setShowAuth(true)} size="lg" className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-lg px-8 shadow-lg hover:shadow-xl transition-all" data-testid="get-started-hero-btn">
              Request Pilot Access <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button variant="outline" size="lg" className="text-lg px-8 border-2 border-slate-300 hover:border-blue-600 hover:text-blue-600" data-testid="watch-demo-btn">
              Schedule Demo
            </Button>
          </div>
          
          {/* Trust Indicators */}
          <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-slate-600 mb-8">
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <span>SOC 2 Type II Certified</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <span>ISO 27001 Compliant</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <span>99.9% Uptime SLA</span>
            </div>
          </div>

          {/* Compliance Badge */}
          <Card className="max-w-4xl mx-auto border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-slate-50">
            <CardContent className="py-6">
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <button
                  onClick={() => setShowStandards(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-full text-sm font-medium transition-all shadow-md hover:shadow-lg flex items-center space-x-2 cursor-pointer"
                >
                  <span>7 Exportable Standards + 10 Tracked</span>
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <span className="text-slate-600">→</span>
                <div className="flex flex-wrap gap-2 justify-center">
                  {["ISO 27001", "HL7 FHIR", "NIST 800-53", "FedRAMP", "ATA iSpec", "GDPR", "PCI-DSS", "HIPAA"].map(standard => (
                    <span key={standard} className="text-xs bg-white px-3 py-1 rounded-full border border-slate-300 text-slate-700">
                      {standard}
                    </span>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white/60">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-6">
              Built For Organizations Where Documentation Accuracy Matters
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              From safety-critical manufacturing procedures to defense technical manuals, Standari maintains compliance without complexity. 
              Whether you're managing quality standards, regulatory requirements, or operational knowledge, 
              <span className="font-semibold text-slate-900"> we handle the details so you can focus on your mission.</span>
            </p>
          </div>

          {/* Proven Results */}
          <div className="grid md:grid-cols-4 gap-6">
            <Card className="doc-card text-center border-2 border-slate-200 hover:border-blue-400 transition-all">
              <CardContent className="pt-8 pb-8">
                <div className="text-5xl font-bold font-heading bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent mb-3">
                  85%
                </div>
                <p className="text-slate-700 font-medium mb-1">Time Savings</p>
                <p className="text-sm text-slate-500">Documentation maintenance</p>
              </CardContent>
            </Card>

            <Card className="doc-card text-center border-2 border-slate-200 hover:border-green-400 transition-all">
              <CardContent className="pt-8 pb-8">
                <div className="text-5xl font-bold font-heading bg-gradient-to-r from-green-600 to-green-800 bg-clip-text text-transparent mb-3">
                  100%
                </div>
                <p className="text-slate-700 font-medium mb-1">Compliance Rate</p>
                <p className="text-sm text-slate-500">Audit-ready exports</p>
              </CardContent>
            </Card>

            <Card className="doc-card text-center border-2 border-slate-200 hover:border-purple-400 transition-all">
              <CardContent className="pt-8 pb-8">
                <div className="text-5xl font-bold font-heading bg-gradient-to-r from-purple-600 to-purple-800 bg-clip-text text-transparent mb-3">
                  10x
                </div>
                <p className="text-slate-700 font-medium mb-1">Faster Updates</p>
                <p className="text-sm text-slate-500">vs. manual processes</p>
              </CardContent>
            </Card>

            <Card className="doc-card text-center border-2 border-slate-200 hover:border-orange-400 transition-all">
              <CardContent className="pt-8 pb-8">
                <div className="text-5xl font-bold font-heading bg-gradient-to-r from-orange-600 to-orange-800 bg-clip-text text-transparent mb-3">
                  50%
                </div>
                <p className="text-slate-700 font-medium mb-1">Faster Onboarding</p>
                <p className="text-sm text-slate-500">Team efficiency gain</p>
              </CardContent>
            </Card>
          </div>

          {/* Trust Statement */}
          <div className="mt-12 text-center">
            <p className="text-lg text-slate-600 italic">
              "Trusted by organizations that can't afford to get documentation wrong."
            </p>
          </div>
        </div>
      </section>

      {/* Core Features Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
              Built for Compliance & Scale
            </h2>
            <p className="text-lg text-slate-600">Handle unstructured inputs. Export to standards. Maintain effortlessly.</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Ingestion */}
            <Card className="doc-card hover:shadow-lg transition-all">
              <CardHeader>
                <Upload className="h-10 w-10 text-blue-600 mb-3" />
                <CardTitle className="font-heading text-lg">Universal Document Processing</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 text-sm mb-3">Process structured and unstructured content from any source: digital files, scanned documents, email threads, and voice recordings</p>
                <Badge variant="secondary" className="text-xs">OCR + NLP</Badge>
              </CardContent>
            </Card>

            {/* Normalization */}
            <Card className="doc-card hover:shadow-lg transition-all">
              <CardHeader>
                <Database className="h-10 w-10 text-blue-600 mb-3" />
                <CardTitle className="font-heading text-lg">Automated Processing</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 text-sm mb-3">Automatically structure documents, correct grammar, validate compliance, and extract terminology</p>
                <Badge variant="secondary" className="text-xs">Advanced</Badge>
              </CardContent>
            </Card>

            {/* 5-Min Revisions */}
            <Card className="doc-card hover:shadow-lg transition-all">
              <CardHeader>
                <RefreshCw className="h-10 w-10 text-green-600 mb-3" />
                <CardTitle className="font-heading text-lg">Rapid Revision Cycle</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 text-sm mb-3">Visual diff review, one-click apply, proactive staleness detection</p>
                <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">5-Minute Updates</Badge>
              </CardContent>
            </Card>

            {/* Export Formats */}
            <Card className="doc-card hover:shadow-lg transition-all">
              <CardHeader>
                <Layers className="h-10 w-10 text-blue-600 mb-3" />
                <CardTitle className="font-heading text-lg">Standards-Native Export</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 text-sm mb-3">PDF, Word, DITA, S1000D (BREX Issue 6), SCORM, xAPI, DocBook</p>
                <Badge variant="secondary" className="text-xs">9+ Formats</Badge>
              </CardContent>
            </Card>

            {/* Integrations */}
            <Card className="doc-card hover:shadow-lg transition-all">
              <CardHeader>
                <Globe className="h-10 w-10 text-blue-600 mb-3" />
                <CardTitle className="font-heading text-lg">Enterprise Integrations</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 text-sm mb-3">Confluence, SharePoint, Google Workspace, Moodle LMS</p>
                <Badge variant="secondary" className="text-xs">API + SSO</Badge>
              </CardContent>
            </Card>

            {/* Collaboration */}
            <Card className="doc-card hover:shadow-lg transition-all">
              <CardHeader>
                <Users className="h-10 w-10 text-blue-600 mb-3" />
                <CardTitle className="font-heading text-lg">Team Collaboration</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 text-sm mb-3">Review workflows, role-based access, real-time comments</p>
                <Badge variant="secondary" className="text-xs">RBAC</Badge>
              </CardContent>
            </Card>

            {/* Translation */}
            <Card className="doc-card hover:shadow-lg transition-all">
              <CardHeader>
                <Globe className="h-10 w-10 text-blue-600 mb-3" />
                <CardTitle className="font-heading text-lg">Multi-Language Support</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 text-sm mb-3">40+ languages with technical accuracy and compliance preservation</p>
                <Badge variant="secondary" className="text-xs">Translation API</Badge>
              </CardContent>
            </Card>

            {/* Security */}
            <Card className="doc-card hover:shadow-lg transition-all">
              <CardHeader>
                <Lock className="h-10 w-10 text-blue-600 mb-3" />
                <CardTitle className="font-heading text-lg">Security & Compliance</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 text-sm mb-3">CMMC 2.0, NIST 800-171, immutable audit trails, on-premise deployment</p>
                <Badge variant="secondary" className="text-xs">FedRAMP Ready</Badge>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Removed Risk Calculator - Keeping It Simple */}

      {/* Pricing */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-white/60">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
              Professional Pricing for Organizations
            </h2>
            <p className="text-lg text-slate-600">Pilot programs available. Project-based or annual licensing.</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Pro */}
            <Card className="border-2 border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading text-2xl">Pro</CardTitle>
                <div className="text-4xl font-bold font-heading text-slate-900 mt-4">
                  $49<span className="text-lg text-slate-600">/mo</span>
                </div>
                <CardDescription>For contractors & small teams</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Unlimited documents</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>100+ templates (OSHA, contracts, invoices)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>PDF & Word export</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Up to 5 team members</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Email support</span>
                  </li>
                </ul>
                <Button className="w-full" variant="outline" onClick={() => setShowAuth(true)}>Start Pro</Button>
              </CardContent>
            </Card>

            {/* Enterprise */}
            <Card className="border-2 border-blue-600 shadow-xl relative">
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white">Most Popular</Badge>
              <CardHeader>
                <CardTitle className="font-heading text-2xl">Enterprise</CardTitle>
                <div className="text-4xl font-bold font-heading text-slate-900 mt-4">
                  $199<span className="text-lg text-slate-600">/user/mo</span>
                </div>
                <CardDescription>For teams & departments</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span><strong>Everything in Pro, plus:</strong></span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>All 7 standards (DITA, S1000D, MIL-STD)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Advanced validation (XSD/BREX)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>SSO integration</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>API access</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Up to 10 team members</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Priority support</span>
                  </li>
                </ul>
                <Button className="w-full bg-gradient-to-r from-blue-600 to-blue-700" onClick={() => setShowAuth(true)}>Start Enterprise</Button>
              </CardContent>
            </Card>

            {/* Enterprise */}
            <Card className="border-2 border-slate-300 shadow-xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-2xl">Enterprise</CardTitle>
                  <Badge variant="outline" className="text-xs">DoD Ready</Badge>
                </div>
                <div className="mt-4">
                  <div className="text-4xl font-bold font-heading text-slate-900">Custom</div>
                  <p className="text-sm text-slate-600 mt-1">Tailored for defense, aerospace & federal</p>
                </div>
                <CardDescription>For DoD contractors, hospitals, federal agencies</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span><strong>Everything in Enterprise, plus:</strong></span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Unlimited team members</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>On-premise deployment</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>FedRAMP compliance path</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Custom integrations</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>White-label options</span>
                  </li>
                </ul>
                <div className="pt-4 border-t border-slate-200">
                  <p className="text-xs text-slate-600 mb-3">Volume discounts: 100+ users: $89/user | 500+ users: $79/user</p>
                  <Button className="w-full" onClick={() => setShowAuth(true)}>Start Enterprise Trial</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl font-heading font-bold text-slate-900 mb-12">
            Trusted By Teams That Move Fast
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            <Card className="doc-card text-center">
              <CardContent className="pt-6">
                <Clock className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <div className="text-4xl font-bold font-heading text-blue-600 mb-2">10x</div>
                <p className="text-slate-600">Faster Updates</p>
              </CardContent>
            </Card>
            <Card className="doc-card text-center">
              <CardContent className="pt-6">
                <TrendingUp className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <div className="text-4xl font-bold font-heading text-green-600 mb-2">85%</div>
                <p className="text-slate-600">Cost Reduction</p>
              </CardContent>
            </Card>
            <Card className="doc-card text-center">
              <CardContent className="pt-6">
                <Shield className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                <div className="text-4xl font-bold font-heading text-purple-600 mb-2">100%</div>
                <p className="text-slate-600">Compliance Rate</p>
              </CardContent>
            </Card>
            <Card className="doc-card text-center">
              <CardContent className="pt-6">
                <Users className="h-12 w-12 text-orange-600 mx-auto mb-4" />
                <div className="text-4xl font-bold font-heading text-orange-600 mb-2">5min</div>
                <p className="text-slate-600">Avg. Update Time</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-6">
            Deploy Standari In Your Infrastructure
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join defense, aerospace, and enterprise teams maintaining compliant documentation at scale.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => setShowAuth(true)} size="lg" className="bg-white text-blue-600 hover:bg-slate-100 text-lg px-8 shadow-lg" data-testid="get-started-cta-btn">
              Request Pilot Access <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button size="lg" variant="outline" className="text-white border-2 border-white hover:bg-white/10 text-lg px-8">
              Schedule Enterprise Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <svg className="h-8 w-8" viewBox="0 0 40 40" fill="none">
                  <path d="M20 2L37 12L37 28L20 38L3 28L3 12L20 2Z" fill="url(#standari-footer-gradient)" stroke="#64748b" strokeWidth="1.5"/>
                  <circle cx="20" cy="20" r="2.5" fill="white"/>
                  <circle cx="20" cy="12" r="1.2" fill="white" opacity="0.7"/>
                  <circle cx="28" cy="16" r="1.2" fill="white" opacity="0.7"/>
                  <circle cx="28" cy="24" r="1.2" fill="white" opacity="0.7"/>
                  <circle cx="20" cy="28" r="1.2" fill="white" opacity="0.7"/>
                  <circle cx="12" cy="24" r="1.2" fill="white" opacity="0.7"/>
                  <circle cx="12" cy="16" r="1.2" fill="white" opacity="0.7"/>
                  <defs>
                    <linearGradient id="standari-footer-gradient" x1="0" y1="0" x2="40" y2="40">
                      <stop offset="0%" stopColor="#475569"/>
                      <stop offset="50%" stopColor="#64748b"/>
                      <stop offset="100%" stopColor="#334155"/>
                    </linearGradient>
                  </defs>
                </svg>
                <span className="text-lg font-heading font-bold text-white">Standari</span>
              </div>
              <p className="text-sm">Documentation that stays current.</p>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">Product</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white">Features</a></li>
                <li><a href="#" className="hover:text-white">Integrations</a></li>
                <li><a href="#" className="hover:text-white">Pricing</a></li>
                <li><a href="#" className="hover:text-white">Security</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">Enterprise</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white">On-Premise</a></li>
                <li><a href="#" className="hover:text-white">Compliance</a></li>
                <li><a href="#" className="hover:text-white">Support</a></li>
                <li><a href="#" className="hover:text-white">Professional Services</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">Resources</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white">Documentation</a></li>
                <li><a href="#" className="hover:text-white">API Reference</a></li>
                <li><a href="#" className="hover:text-white">Case Studies</a></li>
                <li><a href="#" className="hover:text-white">Technical Support</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 space-y-4">
            <p className="text-center text-xs text-slate-500 max-w-4xl mx-auto leading-relaxed">
              <strong className="text-slate-400">Important Notice:</strong> Standari provides automated document processing and format conversion tools for reference and convenience.
              Validation results and compliance checks are advisory only and do not constitute official certification or guarantee of compliance with any standard.
              Users are responsible for ensuring their documents meet official requirements through qualified personnel and authorized certification bodies where applicable.
              Standards-related trademarks and specifications remain the property of their respective organizations.
            </p>
            <p className="text-center text-sm">© 2025 Standari. Single Source of Truth.</p>
          </div>
        </div>
      </footer>

      {/* Standards Modal */}
      {showStandards && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowStandards(false)}>
          <Card className="max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-2xl font-heading">Available Standards</CardTitle>
                  <CardDescription>7 exportable with full validation + 10 tracked for compliance</CardDescription>
                </div>
                <button onClick={() => setShowStandards(false)} className="text-slate-400 hover:text-slate-600">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Exportable Standards */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm mr-3">Exportable</span>
                  Full Export + Validation
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    {name: "DITA 1.3", desc: "Darwin Information Typing", tier: "Enterprise"},
                    {name: "S1000D Issue 6.1", desc: "Technical Publications", tier: "Enterprise"},
                    {name: "MIL-STD-40051", desc: "Technical Manuals", tier: "Enterprise"},
                    {name: "MIL-STD-38784", desc: "Manual Format", tier: "Enterprise"},
                    {name: "ISO/IEC/IEEE 15288", desc: "Systems Engineering", tier: "Enterprise"},
                    {name: "ATA iSpec 2200", desc: "Aviation Maintenance", tier: "Enterprise"},
                    {name: "HL7 FHIR R5", desc: "Healthcare Interoperability", tier: "Enterprise"}
                  ].map((std, idx) => (
                    <div key={idx} className="border border-slate-200 rounded-lg p-4 hover:border-blue-400 transition-colors">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-slate-900">{std.name}</h4>
                        <Badge variant="outline" className="text-xs">{std.tier}</Badge>
                      </div>
                      <p className="text-sm text-slate-600">{std.desc}</p>
                      <div className="mt-3 flex items-center text-xs text-green-600">
                        <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        PDF, Word, XML Export
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tracked Standards */}
              <div className="border-t border-slate-200 pt-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm mr-3">Tracked</span>
                  Compliance Reference Only
                </h3>
                <div className="grid md:grid-cols-3 gap-3">
                  {[
                    "ISO 27001", "GDPR", "PCI-DSS", "HIPAA", "SOX", 
                    "AS9100", "IATF 16949", "NIST 800-53", "FedRAMP", "MIL-STD-31000"
                  ].map((std, idx) => (
                    <div key={idx} className="border border-slate-200 rounded-md p-3 text-sm">
                      <div className="font-medium text-slate-900">{std}</div>
                      <div className="text-xs text-slate-500 mt-1">Compliance info available</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4 text-center">
                <Button onClick={() => {setShowStandards(false); setShowAuth(true);}} className="bg-blue-600 hover:bg-blue-700">
                  Start Pilot to Access →
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Auth Modal */}
      {showAuth && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle>Authentication Required</CardTitle>
              <CardDescription>Please sign in to continue</CardDescription>
            </CardHeader>
            <CardContent>
              <p>Auth modal content goes here...</p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}